import { spawn, type ChildProcess } from "node:child_process";
import { createWriteStream, type WriteStream } from "node:fs";

export interface RunningProcess {
  child: ChildProcess;
  completion: Promise<void>;
  log: WriteStream;
}

export async function runCommand(command: string, cwd: string, logPath: string): Promise<void> {
  const running = startCommand(command, cwd, logPath, true);
  await running.completion.finally(() => running.log.end());
}

export function startCommand(
  command: string,
  cwd: string,
  logPath: string,
  inheritOutput = false,
): RunningProcess {
  const log = createWriteStream(logPath, { flags: "a" });
  log.write(`\n$ ${command}\n`);

  const child = spawn(command, {
    cwd,
    env: process.env,
    shell: true,
    stdio: ["ignore", "pipe", "pipe"],
    detached: process.platform !== "win32",
  });

  for (const stream of [child.stdout, child.stderr]) {
    stream?.pipe(log, { end: false });
    if (inheritOutput) stream?.pipe(process.stderr, { end: false });
  }

  const completion = new Promise<void>((resolve, reject) => {
    child.once("error", reject);
    child.once("close", (code, signal) => {
      if (code === 0) resolve();
      else reject(new Error(`Command failed (${code ?? signal}): ${command}`));
    });
  });

  return { child, completion, log };
}

export async function terminate(running: RunningProcess): Promise<void> {
  if (running.child.exitCode === null && running.child.signalCode === null) {
    if (process.platform === "win32") running.child.kill("SIGTERM");
    else process.kill(-running.child.pid!, "SIGTERM");
  }

  await Promise.race([
    running.completion.catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, 5_000)),
  ]);
  running.log.end();
}
